import dayjs from 'dayjs'
import LocalizationKeys from '../i18n/LocalizationKeys'

export interface Invoice {
  id: string
  number: string
  timestamp: number
  status: string
  download?: string
  amount: string
}

/**
 * Status is forwarded directly from Stripe
 * To read more on status, refer to https://docs.stripe.com/api/subscriptions/object#subscription_object-status
 */
export interface Subscription {
  trialEndsAt: string
  status:
    | 'incomplete'
    | 'incomplete_expired'
    | 'trialing'
    | 'active'
    | 'past_due'
    | 'canceled'
    | 'unpaid'
    | 'paused'
}

export interface Plan {
  name: string
  amount: string
  nextPaymentAt: number
  recurring: {
    interval: string
    intervalCount: number
  }
}

export type PlanStatus =
  | 'trial'
  | 'paused'
  | 'payment_required'
  | 'canceled'
  | 'payment_error'

export interface PlanStatusDetails {
  status: PlanStatus
  color: string
  localizationKey: string
  daysRemaining?: number
}

export const getPlanStatus = (
  subscription: Subscription
): PlanStatusDetails | undefined => {
  switch (subscription.status) {
    case 'trialing':
      const trialEndDate = dayjs(subscription.trialEndsAt)
      const today = dayjs()
      const daysRemaining = trialEndDate.diff(today, 'day')
      return {
        status: 'trial',
        color: 'purple',
        localizationKey:
          LocalizationKeys.Component.Subscription.StatusTag.Trial,
        daysRemaining,
      }
    case 'paused':
      return {
        status: 'paused',
        color: '',
        localizationKey:
          LocalizationKeys.Component.Subscription.StatusTag.Paused,
      }
    case 'past_due':
    case 'unpaid':
      return {
        status: 'payment_required',
        color: 'orange',
        localizationKey:
          LocalizationKeys.Component.Subscription.StatusTag.PaymentRequired,
      }
    case 'canceled':
      return {
        status: 'canceled',
        color: 'red',
        localizationKey:
          LocalizationKeys.Component.Subscription.StatusTag.Canceled,
      }
    case 'incomplete':
    case 'incomplete_expired':
      return {
        status: 'payment_error',
        color: 'red',
        localizationKey:
          LocalizationKeys.Component.Subscription.StatusTag.PaymentError,
      }
    default:
      return
  }
}

export const isSubscriptionValid = (subscription?: Subscription): boolean => {
  if (!subscription) {
    return false
  }
  const valideStatuses: Subscription['status'][] = ['active', 'trialing']
  return valideStatuses.includes(subscription.status)
}
